import React, {useEffect, useRef, useState} from "react";
import {BUNDLE_ICON} from "../Constants";
import {createPortal} from "react-dom";
import ResultsDisplay from "../components/Search/ResultsDisplay";
import {RootState} from "../store/rootReducer";
import {useDispatch, useSelector} from "react-redux";
import SearchCriteria from "../components/Search/SearchCriteria";
import StorefrontBundleModal, {BUNDLE_DOCK_WIDTH} from "../components/ProductBundles/StorefrontBundleModal";
import SelectedFacetDisplay from "../components/Search/SelectedFacetDisplay";
import {
    clearSearchCriteria,
    updateDesiredQuantity,
    updateSearchBarDocked,
    updateSearchCriteria,
    updateSelectedSaveSearchId
} from "../store/searchSlice";
import {
    Callout,
    CommandBar,
    ICommandBarItemProps,
    Icon, IconButton,
    IContextualMenuItem,
    mergeStyleSets,
    Panel,
    PrimaryButton,
    Spinner,
    Text, TooltipHost
} from '@fluentui/react'
import CustomizationModal from "../components/ColumnCustomization/CustomizationModal";
import {
    useGetCatalogsQuery,
    useGetInstalledExtensionsQuery,
    useGetSearchResultsQuery,
    useGetUserSettingsQuery,
    useRunRealtimeExtensionMutation,
    useRunRealtimeSearchExtensionMutation,
    useUpdateProductVisitMutation
} from "../store/Api";
import {
    useGetSavedSearchQuery, useGetSavedSearchRefsQuery,
    useGetTopSavedSearchesQuery,
} from "../store/endpoints/SavedSearchEndpoints";
import {
} from "../store/endpoints/OrganizationsEndpoints"
import {
    IAttribute,
    IFilledFilters,
    IFilter,
    IProduct,
    ISavedSearch,
    ISavedSearchRef,
    ISearchRequest
} from "../models/Search";
import _ from 'lodash';
import DetailsModal from "../components/DetailsModal/DetailsModal";
import useCan, {EDIT_ITEM, SAVED_SEARCH_ADMIN, STOREFRONT_ADMIN} from "../permissions/ability";
import BulkStorefrontPanel from "../components/Storefront/BulkStorefrontPanel";
import ProductSearchBox from "../components/Search/ProductSearchBox";
import SearchPager from "../components/Search/SearchPager";
import SelectedDisplay from "../components/Search/SelectedDisplay";
import NewProductModal from "../components/DetailsModal/NewProductModal";
import ProductIdModal from "../components/DetailsModal/ProductIdModal";
import {IsMutationSuccess} from "../logic/MutationTypeChecker";
import {IRealTimeExtensionResponse} from "../models/Extension";
import {useId} from "@fluentui/react-hooks";
import SearchSettingsSelection from "../components/Search/SearchSettingsSelection";
import SplitKeywordDisplay from "../components/Search/SplitKeywordDisplay";
import SavedSearchSelectorPanel from "../components/Search/SavedSearchSelectorPanel";
import SaveSearchPanel from "../components/Search/SaveSearchPanel";
import ClearSearchDialog from "../components/Search/ClearSearchDialog";
import Footer from "../components/Layout/Footer";
import ConfiguratorSearchTable from "../components/Configurator/Search/ConfiguratorSearchTable";
import ConfiguratorPanel from "../components/Configurator/Product/ConfiguratorPanel";
import { ICollectionResponse } from "../models/ICollectionResponse";
import { INormalizedProduct } from "../models/NormalizedProduct";
import SearchStatus from "../components/Search/SearchStatus";
import SearchSettingsPanel from "../components/Settings/SearchSettings/SearchSettingsPanel";
import {useUrlPanelState} from "../hooks/useUrlState";
import { useMsal } from "@azure/msal-react";
import ProcureProductPanel from "../components/Procurement/ProcureProduct/ProcureProductPanel";
import {useGetProductBundleRefsQuery} from "../store/endpoints/ProductBundleEndpoints";
import {IProductBundleRef} from "../models/ProductBundles";
import ProductBundleSearchTable, {loadPinned as loadBundlesPinned} from "../components/ProductBundles/ProductBundleSearchTable";
import AddToDestinationDialog from "../components/Common/AddToDestinationDialog";
import SavedSearchRefSearchTable from "../components/Search/SavedSearchRefSearchTable";

interface ISearchRequestOverride {
    newKeywords?: string;
    keywords?: string;
    page?: number;
    pageSize?: number;
    inStock?: boolean;
    soldBefore?: boolean;
    lowPrice?: number;
    highPrice?: number;
    manufacturer?: string[];
    vendor?: string[];
    must?: IAttribute[];
    boost?: IAttribute[];
    exclude?: IAttribute[];
    subCategory?: string;
    category?: string;
    segment?: string;
    checkRealtimeSelection?: boolean;
    filters?: IFilter[];
    productLines?: string[];
    tags?: string[];
    userTags?: string[];
    inStockVendors?: string[];
    inCatalogVendors?: string[];
    name?: string;
    sortField?: string;
    inStorefront?: boolean;
    storefrontFilter?: string[];
    includeAllConfigurations?: boolean;
}

const SearchPage: React.FC = () => {
    const canUseSavedSearch = useCan(SAVED_SEARCH_ADMIN);
    const canEditItem = useCan(EDIT_ITEM);

    const user = useMsal().accounts[0]?.username;

    const dispatch = useDispatch();
    const [keywords, setKeywords] = useState<string | undefined>();
    const [page, setPage] = useState<number | undefined>();
    const [pageSize, setPageSize] = useState<number | undefined>();
    const [inStock, setInStock] = useState<boolean>(false);
    const [soldBefore, setSoldBefore] = useState<boolean>(false);
    const [includeAllConfigurations, setIncludeAllConfigurations] = useState<boolean>(false);
    const [lowPrice, setLowPrice] = useState<number | undefined>();
    const [highPrice, setHighPrice] = useState<number | undefined>();
    const [manufacturer, setManufacturer] = useState<string[] | undefined>();
    const [tags, setTags] = useState<string[] | undefined>();
    const [userTags, setUserTags] = useState<string[] | undefined>();
    const [productLines, setProductLines] = useState<string[] | undefined>();
    const [vendor, setVendor] = useState<string[] | undefined>();
    const [mustFilter, setMustFilter] = useState<IAttribute[] | undefined>();
    const [boostFilter, setBoostFilter] = useState<IAttribute[] | undefined>();
    const [excludeFilter, setExcludeFilter] = useState<IAttribute[] | undefined>();
    const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
    const [subCategoryFilter, setSubCategoryFilter] = useState<string | undefined>();
    const [segmentFilter, setSegmentFilter] = useState<string | undefined>();
    const [checkRealtimeSelection, setCheckRealtimeSelection] = useState<boolean | undefined>();
    const [filters, setFilters] = useState<IFilledFilters>({});
    const [hasRunRealtime, setHasRunRealtime] = useState(false);
    const [realtimeErrors, setRealtimeErrors] = useState<{[key:string]: string}>({});
    const [refreshingVendors, setRefreshingVendors] = useState<{[key:string]: string | undefined}>({});
    
    const criteria = useSelector((state: RootState) => state.search.criteria);
    const catalog = useSelector((state: RootState) => state.search.selectedCatalog);
    const desiredQuantity = useSelector((state: RootState) => state.search.desiredQuantity);
    const selectedSavedSearchId = useSelector((state: RootState) => state.search.selectedSaveSearchId);
    const catalogs = useGetCatalogsQuery();
    const results = useGetSearchResultsQuery({catalog: catalog?.name, criteria});
    const extensions = useGetInstalledExtensionsQuery();
    const [runRealtime] = useRunRealtimeExtensionMutation();
    const [updateProductVisit] = useUpdateProductVisitMutation();
    const [selectedProduct, setSelectedProduct] = useState<INormalizedProduct | undefined>();
    const [quicksearchProduct, setQuicksearchProduct] = useState<string | undefined>();
    const [sortVendor, setSortVendor] = useState<string | undefined>();
    const [sortDirection, setSortDirection] = useState<string | undefined>();
    const [inStockVendors, setInStockVendors] = useState<string[] | undefined>();
    const [inCatalogVendors, setInCatalogVendors] = useState<string[] | undefined>();
    const [newProduct, setNewProduct] = useState(false);
    const [nameSearch, setNameSearch] = useState<string | undefined>();
    const errorIconId = useId('error-icon');
    const [showErrorCallout, setShowErrorCallout] = useState(false);
    const [allKeywords, setAllKeywords] = useState<boolean | undefined>(false);
    const [newKeywords, setNewKeywords] = useState<string | undefined>();
    const [splitKeywords, setSplitKeywords] = useState<string[] | undefined>();
    const [splitQuotedKeywords, setSplitQuotedKeywords] = useState<string[] | undefined>();
    const [inStorefront, setInStorefront] = useState<boolean | undefined>();
    const [storefrontFilter, setStorefrontFilter] = useState<string[] | undefined>();
    const [savedSearchesModalOpen, setSavedSearchesModalOpen] = useState(false);
    const [savedSearchDetailsModalOpen, setSavedSearchDetailsModalOpen] = useState(false);
    const [selectedSavedSearch, setSelectedSavedSearch] = useState<ISavedSearch | undefined>();
    const [displayClearCriteriaModal, setDisplayClearCriteriaModal] = useState<boolean>(false);
    const getSavedSearch = useGetSavedSearchQuery({id: selectedSavedSearchId ?? ""}, {skip: (!selectedSavedSearchId || selectedSavedSearchId === '')})
    const userSettings = useGetUserSettingsQuery();
    const topSavedSearches = useGetTopSavedSearchesQuery();
    const bundleRefs = useGetProductBundleRefsQuery({listedInStorefront: criteria.inStorefront, storefronts: criteria.storefrontFilter}, {skip: !criteria});
    const savedSearchRefs = useGetSavedSearchRefsQuery();
    const [bundleCount, setBundleCount] = useState<number>(0);
    // A pinned bundle strip comes back on its own when the page loads; otherwise Bundles starts closed.
    const [showBundles, setShowBundles] = useState(() => loadBundlesPinned());
    const [showSavedSearches, setShowSavedSearches] = useState(false);
    const [savedSearchCount, setSavedSearchCount] = useState<number>(0);
    const [clearToken, setClearToken] = useState<number>(0);

    const [selectedBundle, setSelectedBundle] = useState<IProductBundleRef | undefined>();

    // Bulk "List on Storefront" multi-select (storefront admins only).
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    const [selectedProducts, setSelectedProducts] = useState<INormalizedProduct[]>([]);
    const [selectionResetToken, setSelectionResetToken] = useState<number>(0);
    const [bulkStorefrontPanelOpen, setBulkStorefrontPanelOpen] = useState<boolean>(false);
    const [addToDialogOpen, setAddToDialogOpen] = useState<boolean>(false);

    const clearProductSelection = () => {
        setSelectedProducts([]);
        setSelectionResetToken(t => t + 1);
    };

    // Selection is meaningless once the result set changes.
    useEffect(() => {
        clearProductSelection();
    }, [criteria, catalog?.name]);

    const [showCtoProducts, setShowCtoProducts] = useState(false);
    const [ctoProducts, setCtoProducts] = useState<number>(0);
    const [ctoLoading, setCtoLoading] = useState<boolean>(false);

    const [selectedConfig, setSelectedConfig] = useState<string | undefined>(undefined);
    const [newProductId, setNewProductId] = useState<string | undefined>(undefined);

    const [runSearchExtension] = useRunRealtimeSearchExtensionMutation();
    const [expandingSearch, setExpandingSearch] = useState<boolean>(false);
    const [expandedSearch, setExpandedSearch] = useState<boolean>(false);
    const [expandedResults, setExpandedResults] = useState<ICollectionResponse<IProduct>>();
    // In the URL (?panel=searchSettings) so documentation can link to the settings it holds.
    const searchSettingsPanel = useUrlPanelState('searchSettings');
    // Addressable saved-search details (?panel=savedSearch&instance=<id>) so links and the global
    // search palette can open the panel directly; the bookmark icon keeps its local-state path.
    const savedSearchPanel = useUrlPanelState('savedSearch');
    const searchSettingsOpen = searchSettingsPanel.isOpen;

    const [procureItem, setProcureItem] = useState<{catalog: string; productId: string, vendor?: string} | undefined>(undefined);
    const [procuredInfo, setProcuredInfo] = useState<{demandId: string; orderId?: string} | undefined>(undefined);

    const setDesiredQuantity = (quantity?: number) => {
        dispatch(updateDesiredQuantity(quantity));
    }

    
    
    useEffect(() => {
        if(bundleRefs.data){
            setBundleCount(bundleRefs.data.items.length);
        }
    },[bundleRefs.data])

    useEffect(() => {
        if(getSavedSearch.data){
            setSelectedSavedSearch(getSavedSearch.data);
        }
    }, [getSavedSearch.data]);

    useEffect(() => {
        if(selectedSavedSearch?.criteria){
            dispatch(updateSearchCriteria({...selectedSavedSearch.criteria}));
        }
    }, [selectedSavedSearch]);
    
    useEffect(() => {
        if (!expandedSearch && !results.isLoading && !results.isFetching && (results.data?.products.length ?? 0) <= 3 && criteria.keywords) {
            setExpandingSearch(true);
            runSearchExtension({ request: { ...criteria, page: 1, pageSize: 10 } })
                .then((expandedResults) => {
                    setExpandingSearch(false);
                    setExpandedSearch(true);
                    if (IsMutationSuccess<ICollectionResponse<IProduct>>(expandedResults)) {
                        setExpandedResults(expandedResults.data);
                    }
                });
        }
    }, [results, criteria, runSearchExtension, expandedSearch]);

    const runAllRealtime = async () => {
        if (!hasRunRealtime && !results.isFetching && !results.isLoading && extensions.data && results.data && results.data.products.length > 0) {
            const promises = [];
            for (const ext of extensions.data) {
                if (ext.extensionType === 'realtime' && ext.enabled) {
                    // new guid for runId
                    const runId = _.uniqueId('runId_')
                    // Add to refreshing vendors
                    setRefreshingVendors((v) => {
                        v[ext.vendor] = runId;
                        return v;
                    });
                    promises.push(
                        runRealtime({
                            extension: ext,
                            products: results.data.products.map(p => ({catalog: p.catalog, id: p.id, quantity: desiredQuantity}))
                        }).then((result) => {
                            if (refreshingVendors[ext.vendor] !== runId) return;
                            setRefreshingVendors((v) => {
                                v[ext.vendor] = undefined;
                                return v;
                            })
                            if (IsMutationSuccess<IRealTimeExtensionResponse>(result)) {
                                if (result.data?.success) {
                                    setRealtimeErrors((e) => {
                                        const newErrors = {...e};
                                        delete newErrors[ext.nickname ?? ext.name];
                                        return newErrors;
                                    });
                                    return;
                                }
                                setRealtimeErrors({...realtimeErrors, [ext.nickname ?? ext.name]: result.data?.error ?? 'Unknown Error'});
                                setShowErrorCallout(true);
                                return;
                            }
                            setRealtimeErrors({...realtimeErrors, [ext.nickname ?? ext.name]: 'API Call Failed'});
                            setShowErrorCallout(true);
                        })
                    );
                }
            }
            await Promise.all(promises);
        }
    }

    useEffect(() => {
        if (!hasRunRealtime && !results.isFetching && !results.isLoading && extensions.data && results.data) {
            runAllRealtime().then(() => {
                setHasRunRealtime(true);
            });
        }
    }, [hasRunRealtime, extensions.data, results.isFetching, results.isLoading])

    // When desired quantity changes, run realtime again
    useEffect(() => {
        setHasRunRealtime(false);
    }, [desiredQuantity])

    useEffect(() => {
        setKeywords(criteria.keywords);
        setNewKeywords('');
        if (criteria.keywords) {
            const quotedKeywords = criteria.keywords.match(/"([^"]*)"/g);
            const splitKeywords = criteria.keywords.replace(/"([^"]*)"/g, '').split(' ');
            setSplitKeywords(splitKeywords.filter((k) => k !== ''));
            setSplitQuotedKeywords(quotedKeywords || []); // Add null check and provide a default value
        }
        setPage(criteria.page);
        setPageSize(criteria.pageSize);
        setInStock(criteria.inStock);
        setSoldBefore(criteria.soldBefore ?? false);
        setIncludeAllConfigurations(criteria.includeAllConfigurations ?? false);
        setLowPrice(criteria.lowPrice);
        setHighPrice(criteria.highPrice);
        setManufacturer(criteria.manufacturer);
        setVendor(criteria.vendor);
        setMustFilter(criteria.must);
        setBoostFilter(criteria.boost);
        setExcludeFilter(criteria.exclude);
        setCategoryFilter(criteria.category);
        setSubCategoryFilter(criteria.subCategory);
        setSegmentFilter(criteria.segment);
        setCheckRealtimeSelection(criteria.checkRealtimeSelection);
        setTags(criteria.tags);
        setUserTags(criteria.userTags);
        setProductLines(criteria.productLines);
        setSortVendor(criteria.sortVendor);
        setSortDirection(criteria.sortDirection);
        setInStockVendors(criteria.inStockVendors);
        setInCatalogVendors(criteria.inCatalogVendors);
        setNameSearch(criteria.name);
        setAllKeywords(criteria.allKeywords);
        const filledFilters: IFilledFilters = {};
        if(criteria.filters) {
            for (const filter of criteria.filters) {
                if ((filter.max != undefined && filter.max !== 0) || (filter.min != undefined && filter.min !== 0))
                    filledFilters[filter.name] = {min: filter.min, max: filter.max};
            }
        }
        setFilters(filledFilters);
        setHasRunRealtime(false);
        setInStorefront(criteria.inStorefront);
        setStorefrontFilter(criteria.storefrontFilter);
    }, [criteria]);
    
    const search = (criteriaOverride?: ISearchRequestOverride | undefined) => {
        const kw = userSettings.data?.keywordToTags
            ? (criteriaOverride?.keywords ?? keywords) + ' ' + (criteriaOverride?.newKeywords ?? newKeywords)
            : criteriaOverride?.keywords ?? keywords;
        const newCriteria: ISearchRequest = {
            keywords: kw,
            page: criteriaOverride?.page ?? page,
            pageSize: criteriaOverride?.pageSize ?? pageSize,
            inStock: criteriaOverride?.inStock ?? inStock,
            // Absent rather than false so a plain search does not carry a redundant key.
            soldBefore: (criteriaOverride?.soldBefore ?? soldBefore) || undefined,
            includeAllConfigurations: criteriaOverride?.includeAllConfigurations ?? includeAllConfigurations,
            lowPrice: criteriaOverride?.lowPrice ?? lowPrice,
            highPrice: criteriaOverride?.highPrice ?? highPrice,
            manufacturer: criteriaOverride?.manufacturer ?? manufacturer ?? [],
            vendor: criteriaOverride?.vendor ?? vendor ?? [],
            must: criteriaOverride?.must ?? mustFilter ?? [],
            boost: criteriaOverride?.boost ?? boostFilter ?? [],
            exclude: criteriaOverride?.exclude ?? excludeFilter ?? [],
            subCategory: criteriaOverride?.subCategory ?? subCategoryFilter,
            category: criteriaOverride?.category ?? categoryFilter,
            segment: criteriaOverride?.segment ?? segmentFilter,
            userTags: criteriaOverride?.userTags ?? userTags,
            tags: criteriaOverride?.tags ?? tags,
            productLines: criteriaOverride?.productLines ?? productLines,
            sortVendor: sortVendor,
            sortDirection: sortDirection,
            checkRealtimeSelection,
            inCatalogVendors: criteriaOverride?.inCatalogVendors ?? inCatalogVendors,
            inStockVendors: criteriaOverride?.inStockVendors ?? inStockVendors,
            filters: Object.entries(filters).map(([key, value]) => ({
                name: key,
                min: value.min,
                max: value.max,
            })),
            name: criteriaOverride?.name ?? nameSearch,
            allKeywords: allKeywords,
            inStorefront: criteriaOverride?.inStorefront ?? inStorefront,
            storefrontFilter: criteriaOverride?.storefrontFilter ?? storefrontFilter,
        }
        // Split the keywords into quoted strings, and space seperated strings
        if (newCriteria.keywords) {
            const quotedKeywords = newCriteria.keywords.match(/"([^"]*)"/g);
            const splitKeywords = newCriteria.keywords.replace(/"([^"]*)"/g, '').split(' ');
            setSplitKeywords(splitKeywords.filter((k) => k !== ''));
            setSplitQuotedKeywords(quotedKeywords || []); // Add null check and provide a default value
            setNewKeywords('');
        }
        if (_.isEqual(newCriteria, criteria)) results.refetch();
        else dispatch(updateSearchCriteria(newCriteria));
        setHasRunRealtime(false);
        setExpandedSearch(false);
        setExpandedResults(undefined);
    }
    
    // The ProductSearchBox hands back fully-merged criteria (it owns the sugar-expressible
    // fields and passes the rest through) - same tail as search(): split-keyword bookkeeping,
    // dispatch-or-refetch, realtime reset.
    const applyComposedCriteria = (newCriteria: ISearchRequest) => {
        if (newCriteria.keywords) {
            const quotedKeywords = newCriteria.keywords.match(/"([^"]*)"/g);
            const split = newCriteria.keywords.replace(/"([^"]*)"/g, '').split(' ');
            setSplitKeywords(split.filter((k) => k !== ''));
            setSplitQuotedKeywords(quotedKeywords || []);
            setNewKeywords('');
        }
        if (_.isEqual(newCriteria, criteria)) results.refetch();
        else dispatch(updateSearchCriteria(newCriteria));
        setHasRunRealtime(false);
        setExpandedSearch(false);
        setExpandedResults(undefined);
    };

    const openNewProductDetailsPanel = (id: string) => {
        setNewProduct(false);
        setNewProductId(id);
    }

    const [menuOpen, setMenuOpen] = useState(true);
    // A bundle being edited beside the results. Opening it tucks the criteria panel away (both dock on
    // the right); closing it brings the criteria back if they were showing.
    const [dockedBundle, setDockedBundle] = useState<IProductBundleRef | undefined>();
    // Height of the pinned bundle strip (0 when unpinned): the results header sticks below it.
    const [pinnedBundlesHeight, setPinnedBundlesHeight] = useState(0);
    // Mirrored into a ref for the scroll listener below, which is bound once.
    const pinnedBundlesHeightRef = useRef(0);
    pinnedBundlesHeightRef.current = pinnedBundlesHeight;
    const criteriaHiddenForBundle = useRef(false);
    const openDockedBundle = (bundle: IProductBundleRef) => {
        if (menuOpen) { criteriaHiddenForBundle.current = true; setMenuOpen(false); }
        setDockedBundle(bundle);
    };
    const closeDockedBundle = () => {
        setDockedBundle(undefined);
        if (criteriaHiddenForBundle.current) { criteriaHiddenForBundle.current = false; setMenuOpen(true); }
    };
    // Showing the criteria again while a bundle is docked closes the bundle: they share the right edge.
    useEffect(() => { if (menuOpen && dockedBundle) { criteriaHiddenForBundle.current = false; setDockedBundle(undefined); } }, [menuOpen]);

    // Reserve the page scrollbar's width even while there is no scrollbar. The results grid
    // re-fits its columns to the width it gets, and row heights follow the column widths, so a
    // page sitting right at the viewport height could otherwise strobe: the scrollbar appears,
    // the narrower grid re-wraps shorter, the scrollbar goes away, and so on every frame.
    useEffect(() => {
        const html = document.documentElement;
        const previous = html.style.scrollbarGutter;
        html.style.scrollbarGutter = 'stable';
        return () => { html.style.scrollbarGutter = previous; };
    }, []);

    // One definition, two homes each: the search box and the pager render inline normally and
    // portal into the header's dock slots (see NavMenu) once the page scrolls past the box.
    const searchPager = (
        <SearchPager
            page={page ?? 1}
            pageSize={pageSize ?? 50}
            total={results.data?.total ?? 0}
            onPageChange={(p) => {
                setPage(p);
                search({page: p});
            }}
            onPageSizeChange={(size) => search({pageSize: size, page: 1})}
        />
    );
    const searchBoxProps = {
        catalogName: catalog?.name,
        onKeywordChange: (k: string) => {
            if (userSettings.data?.keywordToTags) setNewKeywords(k);
            else setKeywords(k);
        },
        onApply: applyComposedCriteria,
        onProductSelected: setQuicksearchProduct,
    };
    const productSearchBox = <ProductSearchBox {...searchBoxProps} />;

    const searchBarDocked = useSelector((state: RootState) => state.search.searchBarDocked === true);
    const searchBoxAnchorRef = useRef<HTMLDivElement>(null);
    const dockedRef = useRef(false);
    // Dock once the inline box fully scrolls under the fixed 48px header. A synchronous scroll
    // listener, not an IntersectionObserver: IO delivers asynchronously and trails a fast
    // scroll by a frame or two, which reads as the box blinking out before it docks. React 17
    // renders dispatches from native listeners synchronously, so this swap lands within the
    // scroll event itself.
    useEffect(() => {
        const check = () => {
            const anchor = searchBoxAnchorRef.current;
            if (!anchor) return;
            // The pinned bundle strip (fixed under the header) counts as header for docking purposes.
            const shouldDock = anchor.getBoundingClientRect().bottom <= 48 + pinnedBundlesHeightRef.current;
            if (shouldDock !== dockedRef.current) {
                dockedRef.current = shouldDock;
                dispatch(updateSearchBarDocked(shouldDock));
            }
        };
        check();
        window.addEventListener('scroll', check, {passive: true});
        window.addEventListener('resize', check);
        return () => {
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            dispatch(updateSearchBarDocked(false));
        };
    }, [dispatch]);
    // The dock divs are always mounted in the header (hidden while empty), so resolve them
    // once up front — that way the dock flip and the portal render share ONE commit. The
    // interval only guards against the CommandBar remounting them on a re-measure.
    const [searchDockEl, setSearchDockEl] = useState<HTMLElement | null>(null);
    const [pagerDockEl, setPagerDockEl] = useState<HTMLElement | null>(null);
    useEffect(() => {
        const resolve = () => {
            setSearchDockEl((cur) => {
                const el = document.getElementById('header-search-dock');
                return cur === el ? cur : el;
            });
            setPagerDockEl((cur) => {
                const el = document.getElementById('header-pager-dock');
                return cur === el ? cur : el;
            });
        };
        resolve();
        const interval = window.setInterval(resolve, 500);
        return () => window.clearInterval(interval);
    }, []);
    const [editColumnsModalOpen, setEditColumnsModalOpen] = useState(false);
    
    const getCommands = () => {
            return ([criteriaCommandBarItem, customizeCommandBarItem])
    }
    
    const criteriaCommandBarItem: ICommandBarItemProps =
    {
        key: 'criteria',
            text: 'Search Criteria',
        subMenuProps:{
        items:[
            {
                key: 'toggleCriteria',
                iconProps: {iconName: menuOpen ? 'Hide2' : 'View'},
                text: menuOpen ? 'Hide Search Criteria' : 'Show Search Criteria',
                onClick: () => setMenuOpen(!menuOpen)
            },
            {
                key: 'clearCriteria',
                text: 'Clear Search Criteria',
                iconProps: { iconName: 'ClearFilter' },
                onClick: () => {
                    setDisplayClearCriteriaModal(true);
                }
            }
        ]
    },
        split: true,
        iconProps: {iconName: 'Filter'},
        onClick: () => setMenuOpen(!menuOpen),

    }
    
    const customizeCommandBarItem: ICommandBarItemProps =         
        {
            key: 'customize',
            text: 'Customize Columns',
            iconProps: {iconName: 'edit'},
            onClick: () => setEditColumnsModalOpen(true),
        }
    

    const rightCommands: ICommandBarItemProps[] = [
        {
            key: 'cto',
            onRenderIcon: () => ctoLoading ? <Spinner /> : <Icon iconName='ProductVariant' styles={{root: {marginRight: 4, color: showCtoProducts ? '#ffffff' : '#789c2f'}}} />,
            text: `Configurations ${!ctoLoading && ctoProducts ? `(${ctoProducts})` : ''}`,
            // Lights up like Bundles / Saved Searches while its strip is showing.
            buttonStyles: { root: { backgroundColor: showCtoProducts ? 'rgb(120, 156, 47)': "unset", color: showCtoProducts ? '#ffffff' : 'unset'}, label: { fontWeight: ctoProducts > 0 ? 'bold' : undefined } },
            onClick: () => {
                setShowCtoProducts(!showCtoProducts);
            }
        },
        {
            key: 'bundles',
            onRenderIcon: () => (bundleRefs.isLoading || bundleRefs.isFetching) ? <Spinner /> : <Icon iconName={BUNDLE_ICON} styles={{root: {marginRight: 4, color: showBundles ? '#ffffff' : '#789c2f'}}} />,
            text: `Bundles ${!(bundleRefs.isLoading || bundleRefs.isFetching) && bundleCount ? `(${bundleCount})` : ''}`,
            buttonStyles: { root: { backgroundColor: showBundles ? 'rgb(120, 156, 47)': "unset", color: showBundles ? '#ffffff' : 'unset'},label: { fontWeight: bundleCount > 0 ? 'bold' : undefined } },
            onClick: () => {
                setShowBundles(!showBundles);
            }
        }
    ];
    
    // Always present, disabled until something is selected, rather than appearing on first selection.
    // A control that materializes mid-interaction reflows the whole command bar under the pointer and
    // gives no hint the action exists until you have already found it another way; greyed-out advertises
    // it and shows what selecting will unlock. The count stays in the label so the bar still says how
    // much is about to be acted on, and is dropped at zero rather than reading "(0)".
    rightCommands.unshift({
        key: 'addToDestination',
        text: selectedProducts.length > 0 ? `Add To... (${selectedProducts.length})` : 'Add To...',
        iconProps: {iconName: 'Add'},
        disabled: selectedProducts.length === 0,
        onClick: () => setAddToDialogOpen(true)
    });

    // The pricing status sits at the far left of the right-hand group, ahead of Add To / Configurations / Bundles.
    rightCommands.unshift({
        key: 'status',
        text: hasRunRealtime ? '' : 'Updating Pricing',
        onRender: () => <SearchStatus runningRealtime={!hasRunRealtime} expandingSearch={expandingSearch} />
    });
    if(canUseSavedSearch){
        rightCommands.push(
        {
            key: 'savedSearches',
            onRenderIcon: () => (savedSearchRefs.isLoading || savedSearchRefs.isFetching) ? <Spinner /> : <Icon iconName='SearchBookmark' styles={{root: {marginRight: 4, color: showSavedSearches ? '#ffffff' : '#789c2f'}}} />,
            text: `Saved Searches ${!(savedSearchRefs.isLoading || savedSearchRefs.isFetching) && savedSearchCount ? `(${savedSearchCount})` : ''}`,
            buttonStyles: { root: { backgroundColor: showSavedSearches ? 'rgb(120, 156, 47)': "unset", color: showSavedSearches ? '#ffffff' : 'unset'},label: { fontWeight: savedSearchCount > 0 ? 'bold' : undefined } },
            onClick: () => {
                setShowSavedSearches(!showSavedSearches);
            }
        })
    }
    if (Object.keys(realtimeErrors).length > 0) {
        rightCommands.push({
            key: 'errors',
            id: errorIconId,
            onRenderIcon: () => <Icon
                iconName={'Warning'}
                style={{color: '#ff0000'}}
            />,
            iconOnly: true,
            onClick: () => setShowErrorCallout(true)
        });
    }
    rightCommands.push({
        key: 'page',
        onRender: () => searchPager,
    });
    const rightSubMenuItems: IContextualMenuItem[] = [
        {
            key: 'settings',
            text: 'Search Settings',
            iconProps: {iconName: 'Settings'},
            onClick: () => {
                searchSettingsPanel.open();
            }
        }
    ];
    if (canEditItem) {
        rightSubMenuItems.push({
            key: 'create',
            text: 'Create New Item',
            iconProps: {iconName: 'Add'},
            onClick: () => {
                setNewProduct(true);
            }
        });
    }
    if (canStorefrontAdmin) {
        rightSubMenuItems.push({
            key: 'bulkStorefront',
            text: selectedProducts.length > 0
                ? `List on Storefront (${selectedProducts.length})`
                : 'List on Storefront',
            iconProps: {iconName: 'Shop'},
            disabled: selectedProducts.length === 0,
            title: selectedProducts.length === 0 ? 'Select products in the results to list them on a storefront' : undefined,
            onClick: () => {
                setBulkStorefrontPanelOpen(true);
            }
        });
    }

    rightCommands.push({
        key: 'more',
        text: '',
        iconProps: {iconName: 'MoreVertical'},
        iconOnly: true,
        onRenderIcon: () => null,
        subMenuProps: { items: rightSubMenuItems }
    })


    const contentStyles = mergeStyleSets({
        root: {
            zIndex: '10 !important',
            marginTop: '48px',
        },
        main: {
            marginTop: '48px',
        },
        scrollableContent: {
            backgroundColor: '#e2e9cb',
        },
        navigation: {
            backgroundColor: '#e2e9cb',
        },
        footerInner: {
            backgroundColor: '#e2e9cb',
        },
    });
    
    const applySavedSearch = (savedSearchId: string) => {
        dispatch(updateSelectedSaveSearchId(savedSearchId));
    }
    

    const header = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: 'rgb(226, 233, 203)'}}>
            <div style={{display: 'inline-flex', alignItems: 'center'}}>
                {selectedSavedSearch?.name && 
                <TooltipHost content={`Saved search ${selectedSavedSearch.name} applied`}>
                    <IconButton iconProps={{iconName: 'SingleBookmarkSolid'}} style={{paddingRight: '15px'}} onClick={() => setSavedSearchDetailsModalOpen(true)}/>
                </TooltipHost>
                }
                <h2>Search Criteria</h2>

            </div>
        </div>
    )
    
    const clearEverything = () => {
        setClearToken(t => t + 1);
        dispatch(clearSearchCriteria());
        dispatch(updateSelectedSaveSearchId(''));
        setSelectedSavedSearch({})
    }
    
    return (
        // paddingTop doubles .main-content's 1em: extra breathing room between the header and
        // the search bar, scoped to this page only.
        <div className='main-content' style={{ paddingTop: pinnedBundlesHeight > 0 ? `calc(${Math.ceil(pinnedBundlesHeight)}px + 1em)` : '2em' }} key={'search-main-content'}>
                <Panel
                    onRenderHeader={header}
                    headerText='Search Criteria'
                    isOpen={menuOpen}
                    onDismiss={() => setMenuOpen(false)}
                    closeButtonAriaLabel='Close'
                    isBlocking={false}
                    isHiddenOnDismiss={true}
                    isFooterAtBottom={true}
                    layerProps={{ className: 'search-panel' }}
                    styles={{
                        root: {
                            zIndex: 10,
                            marginTop: '48px',
                        },
                        commands:{
                            backgroundColor: 'rgb(226, 233, 203)'
                        },
                        scrollableContent: contentStyles.scrollableContent,
                        navigation: contentStyles.navigation,
                        footerInner: contentStyles.footerInner,
                    }}
                    onRenderFooterContent={() => {
                        return <div>
                            <PrimaryButton style={{ width: '100%' }} onClick={() => {setPage(1); search({page: 1})}} >
                                Apply
                            </PrimaryButton>
                        </div>
                    }}
                // focusTrapZoneProps={focusTrapZoneProps}
                >
                    <SearchSettingsSelection
                        onDesiredQuantityChange={setDesiredQuantity}
                        desiredQuantity={desiredQuantity}
                        allKeywords={allKeywords ?? false}
                        onAllKeywords={setAllKeywords}
                        includeAllConfigurations={criteria.includeAllConfigurations ?? false}
                        onIncludeAllConfigurations={setIncludeAllConfigurations}
                    />
                    <SearchCriteria
                        inStock={inStock}
                        onInStockChange={setInStock}
                        soldBefore={soldBefore}
                        onSoldBeforeChange={setSoldBefore}
                        checkRealtime={checkRealtimeSelection}
                        onCheckRealtimeChange={setCheckRealtimeSelection}
                        category={categoryFilter}
                        onCategoryChange={(s,c) => {
                            setSegmentFilter(s);
                            setCategoryFilter(c);
                        }}
                        segment={segmentFilter}
                        onSegmentChange={setSegmentFilter}
                        mustFilter={mustFilter}
                        boostFilter={boostFilter}
                        excludeFilter={excludeFilter}
                        onFacetsChange={(m,b,e) => {
                            setMustFilter(m);
                            setBoostFilter(b);
                            setExcludeFilter(e);
                        }}
                        manufacturers={manufacturer ?? []}
                        onManufacturerChange={setManufacturer}
                        results={results.data}
                        lowPrice={lowPrice}
                        highPrice={highPrice}
                        onPriceChange={(l,h) => {
                            setLowPrice(l);
                            setHighPrice(h);
                        }}
                        rangeFilters={filters ?? {}}
                        onRangeFilterChange={setFilters}
                        inStockVendors={inStockVendors}
                        inCatalogVendors={inCatalogVendors}
                        onVendorChange={(inStock, inCatalog) => {
                            setInStockVendors(inStock);
                            setInCatalogVendors(inCatalog);
                        }}
                        storefrontFilter={storefrontFilter}
                        onStorefrontFilterChange={setStorefrontFilter}
                        inStorefront={inStorefront}
                        onInStorefrontChange={setInStorefront}
                    />
                </Panel>
                {dockedBundle?.id && (
                    <StorefrontBundleModal
                        docked
                        bundleId={dockedBundle.id}
                        isOpen={true}
                        onDismiss={closeDockedBundle}
                        selectedProducts={selectedProducts}
                    />
                )}
                <div style={{ marginRight: menuOpen ? '340px' : dockedBundle ? `${BUNDLE_DOCK_WIDTH}px` : '0', transition: 'margin-right 0.25s ease' }} key={'searchbox-container'}>
                    {/* The anchor keeps its height while the box is docked in the header, so
                        the IntersectionObserver geometry stays stable (no dock/undock loop). */}
                    <div ref={searchBoxAnchorRef} style={{minHeight: 38}}>
                        {searchBarDocked && searchDockEl ? null : productSearchBox}
                    </div>
                    {searchBarDocked && searchDockEl && createPortal(<ProductSearchBox {...searchBoxProps} docked />, searchDockEl)}
                    {searchBarDocked && pagerDockEl && createPortal(searchPager, pagerDockEl)}
                    <CommandBar
                        items={getCommands()}
                        farItems={rightCommands}
                    />
                    <div key={'selectedFacet-container'}>
                        {/* Must/boost attributes render as chips in the ProductSearchBox now;
                            this row keeps excludes visible (no sugar exists for exclude). */}
                        <SelectedFacetDisplay
                            mustFilter={[]}
                            boostFilter={[]}
                            excludeFilter={excludeFilter}
                            onFacetChange={(m, b, e) => {
                                setExcludeFilter(e);
                                search({exclude: e, page: 1});
                            }}
                        />
                        <SelectedDisplay
                            prefix='Name'
                            selections={nameSearch ? [nameSearch] : []}
                            onChange={(name: React.SetStateAction<string[] | undefined>) => {
                                setNameSearch(undefined);
                                search({name: ''});
                            }}
                        />
                        <SelectedDisplay
                            prefix="Product Lines"
                            selections={productLines ?? []}
                            onChange={(pl: React.SetStateAction<string[] | undefined>) => setProductLines(pl)}
                        />
                        <SelectedDisplay
                            prefix="Tags"
                            selections={tags ?? []}
                            onChange={(tags: React.SetStateAction<string[] | undefined>) => setTags(tags)}
                        />
                        <SelectedDisplay
                            prefix="My Tags"
                            selections={userTags ?? []}
                            onChange={(tags: React.SetStateAction<string[] | undefined>) => setUserTags(tags)}
                        />
                        {userSettings.data?.keywordToTags && <SplitKeywordDisplay
                            keywords={splitKeywords ?? []}
                            onChange={(nk) => {
                                const newKeywordsString = nk.join(' ') + ' ' + (splitQuotedKeywords ?? []).join(' ');
                                setKeywords(newKeywordsString);
                                search({keywords: newKeywordsString, page:1});
                            }}
                        />}
                        {userSettings.data?.keywordToTags && <SplitKeywordDisplay
                            keywords={splitQuotedKeywords ?? []}
                            quoted
                            onChange={(nk) => {
                                const newKeywordsString = (splitKeywords ?? []).join(' ') + ' ' + nk.join(' ');
                                setKeywords(newKeywordsString);
                                search({keywords: newKeywordsString, page:1});
                            }}
                        />}
                    </div>
                    <div className="search-results" style={{overflow: 'auto'}}>
                        <ConfiguratorSearchTable
                            searchCriteria={criteria}
                            onResultsCountChange={setCtoProducts}
                            showResults={showCtoProducts}
                            onLoading={setCtoLoading}
                            onHide={() => setShowCtoProducts(false)}
                        />
                        <ProductBundleSearchTable
                            onOpenBundle={openDockedBundle}
                            selectedProducts={selectedProducts}
                            onPinnedHeightChange={setPinnedBundlesHeight}
                            pinnedRightInset={menuOpen ? 340 : dockedBundle ? BUNDLE_DOCK_WIDTH : 0}
                            searchCriteria={criteria}
                            onResultsCountChange={setBundleCount}
                            showResults={showBundles}
                            onSelectedBundleChange={setSelectedBundle}
                            onCreateNewBundle={(newBundle) => setSelectedBundle(newBundle)}
                            onHide={() => setShowBundles(false)}
                        />
                        <SavedSearchRefSearchTable
                            selectedSaveSearch={selectedSavedSearch}
                            showResults={showSavedSearches}
                            onResultsCountChange={setSavedSearchCount}
                            onSelectedSearchChange={(s) => s?.id && applySavedSearch(s.id)}
                            onHide={() => setShowSavedSearches(false)}
                            currentUsername={user}
                            currentCriteria={criteria}
                            clearToken={clearToken}
                        />

                        <ResultsDisplay
                            // Tucks a pixel under the pinned strip (which stacks above), so a fractional strip height leaves no seam.
                            stickyTop={pinnedBundlesHeight > 0 ? 48 + Math.floor(pinnedBundlesHeight) - 1 : 48}
                            expandedResults={expandedResults}
                            expandingSearch={expandingSearch}
                            // runningRealtime={!hasRunRealtime}
                            refreshingVendors={Object.entries(refreshingVendors).filter(([_,v]) => v != undefined).map(([k,_]) => k)}
                            onDetailsSelection={(p) => {
                                updateProductVisit({catalog: p.catalog, id: p.id})
                                setSelectedProduct(p)}}
                            onFilterChange={setFilters}
                            selectedBundle={selectedBundle}
                            onSelectionChanged={setSelectedProducts}
                            selectionResetToken={selectionResetToken}
                        />
                    </div>
                    <Footer className={'powered-by-footer-absolute'}/>
                    {(selectedProduct != null) && 
                        <DetailsModal
                            product={selectedProduct.id} 
                            catalog={selectedProduct.catalog} 
                            refreshingVendors={!hasRunRealtime} open={true} 
                            onClose={() => setSelectedProduct(undefined)} 
                            preventEdit={!canEditItem}
                            showConfigurationButton
                            configure={(id) => {
                                setSelectedConfig(id);
                                setSelectedProduct(undefined);
                            }}
                            onProcure={(catalog, id, vendor) => {
                                setProcureItem({catalog, productId: id, vendor});
                                setSelectedProduct(undefined);
                            }}
                    />}
                    {/*new product details modal*/}
                    {(newProductId != null) &&
                        <DetailsModal
                            product={newProductId}
                            catalog={catalog?.name ?? 'technology'}
                            refreshingVendors={!hasRunRealtime} open={true}
                            onClose={() => setNewProductId(undefined)}
                            preventEdit={!canEditItem}
                            showConfigurationButton
                            configure={(id) => {
                                setSelectedConfig(id);
                                setSelectedProduct(undefined);
                            }}
                        />}
                    {bulkStorefrontPanelOpen &&
                        <BulkStorefrontPanel
                            isOpen={bulkStorefrontPanelOpen}
                            onDismiss={() => setBulkStorefrontPanelOpen(false)}
                            catalog={catalog?.name ?? 'technology'}
                            productIds={selectedProducts.map(p => p.id)}
                            onApplied={clearProductSelection}
                        />}
                    {addToDialogOpen &&
                        <AddToDestinationDialog
                            isOpen={addToDialogOpen}
                            onDismiss={() => setAddToDialogOpen(false)}
                            products={selectedProducts}
                            catalog={catalog?.name ?? 'technology'}
                            onAdded={clearProductSelection}
                        />}
                    <NewProductModal open={newProduct} onClose={() => setNewProduct(false)} onSave={(id: string) => openNewProductDetailsPanel(id)}/>
                    {procureItem && <ProcureProductPanel
                        catalog={procureItem?.catalog}
                        productId={procureItem?.productId}
                        vendor={procureItem?.vendor}
                        onDismiss={() => setProcureItem(undefined)}
                        onProcured={(demandId, orderId) => {
                            setProcureItem(undefined);
                            setProcuredInfo({demandId, orderId});
                        }}
                        />}
                </div>
                <CustomizationModal
                    page='search'
                    isOpen={editColumnsModalOpen}
                    onClose={() => setEditColumnsModalOpen(false)}
                />
                <ClearSearchDialog 
                    displayModal={displayClearCriteriaModal} 
                    onClear={clearEverything} 
                    closer={() => {setDisplayClearCriteriaModal(false)}}
                />
                {(savedSearchDetailsModalOpen || savedSearchPanel.isOpen) && 
                <SaveSearchPanel 
                    closer={() => {
                        setSavedSearchDetailsModalOpen(false);
                        savedSearchPanel.close();
                    }}
                    onUpdate={(id) => {
                        dispatch(updateSelectedSaveSearchId(id))}
                    }
                    criteria={criteria}
                    savedSearchId={savedSearchPanel.isOpen ? savedSearchPanel.instanceId : selectedSavedSearchId}
                />
                }
                <SavedSearchSelectorPanel 
                    displayModal={savedSearchesModalOpen} 
                    onSelect={applySavedSearch} 
                    closer={() => setSavedSearchesModalOpen(false)}
                    onClear={clearEverything}
                />
                {quicksearchProduct && (catalog || catalogs.data) &&
                    <ProductIdModal
                        catalog={catalog?.name ?? catalogs.data?.[0].name ?? ''}
                        productId={quicksearchProduct}
                        onDismiss={setQuicksearchProduct}
                    />
                }
                {showErrorCallout && <Callout
                    target={`#${errorIconId}`}
                    onDismiss={() => setShowErrorCallout(false)}
                    setInitialFocus
                    style={{padding: '1em', backgroundColor: '#f8d1d1'}}
                    styles={{
                        beak: { backgroundColor: '#f8d1d1' },
                    }}
                >
                    <Text as='h1' block variant={'xLarge'}>There was an error</Text>
                    {Object.entries(realtimeErrors).map(([key, value]) => {
                        return <Text key={key} as='p' block variant={'mediumPlus'}>The extension {key} returned: {value}</Text>
                    })}
                </Callout>}
                {selectedConfig && <ConfiguratorPanel
                    configId={selectedConfig}
                    onClose={() => setSelectedConfig(undefined)}
                    />}
                    {searchSettingsOpen && <SearchSettingsPanel
                        isOpen={searchSettingsOpen}
                        onClose={() => searchSettingsPanel.close()}
                    />}
            </div>
        );
}

export default SearchPage;